import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"

import styled from "styled-components/native"

import { joinContributorNames } from "@treefort/lib/contributor"
import { formatDate } from "@treefort/lib/date"
import icons from "@treefort/tokens/app/icons"

import useAppManifest from "../../hooks/use-app-manifest"
import { useAsyncViewPropsForQueries } from "../../hooks/use-async-view-props-for-queries"
import useContent from "../../hooks/use-content"
import { useOfflineState } from "../../hooks/use-offline-state"
import { useOpenCheckoutPage } from "../../hooks/use-open-checkout-page"
import {
  canDownloadConsumableContent,
  getConsumableContentFromVideoResponse,
} from "../../lib/consumable-content"
import { createStyle } from "../../lib/create-style"
import DownloadItem from "../../lib/download-item"
import { kebabCase } from "../../lib/kebab-case"
import { syncManager } from "../../watermelon/sync"
import AddToLibraryButton, { canAddToLibrary } from "../add-to-library-button"
import { AppLink } from "../app-link"
import AsyncView, { useAsyncViewProps } from "../async-view"
import { AsyncViewOfflineProvider } from "../async-view-offline-provider"
import Box, { BoxPadding } from "../box"
import CenteredContent from "../centered-content"
import DownloadButton from "../download-button"
import { Heading } from "../heading"
import Icon from "../icon"
import { ListenIconButton } from "../listen-icon-button"
import LockedContentButton from "../locked-content-button"
import { MetadataSpacer, MetadataText } from "../metadata"
import ProgressForConsumableContent from "../progress-for-consumable-content"
import Row from "../row"
import Spacer from "../spacer"
import Text from "../text"
import TextToggle from "../text-toggle"
import { useTokens } from "../tokens-provider"
import VideoPlayerForConsumableContent from "../video-player-for-consumable-content"

// This visually aligns the left edge of the action buttons with the left edge
// of the video title and description. This is necessary because the action
// buttons have padding built-in.
const ActionButtonContainer = styled(Box)`
  margin-left: -${(props) => props.theme.spacing.xsmall}px;
`

const StyledAppLink = styled(AppLink)`
  width: ${({ theme }) => theme.button.height.medium}px;
  height: ${({ theme }) => theme.button.height.medium}px;
  align-items: center;
  justify-content: center;
`

export function VideoModule({
  contentId,
  maxWidth,
  paddingTop = "medium",
  presentation = "solo",
}: {
  contentId: number
  maxWidth?: number
  paddingTop?: BoxPadding
  presentation?: "solo" | "listItem"
}): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const { tokens } = useTokens()
  const [offline] = useOfflineState()
  const videoQueryEnabled = !offline
  const videoQuery = useContent(contentId, "video", {
    enabled: videoQueryEnabled,
  })
  const openCheckoutPage = useOpenCheckoutPage({
    availability: videoQuery.data?.availability,
    contentId,
  })

  // Pull playable content from the API when online and from the local store
  // when offline.
  const consumableContentQuery = useQuery(
    ["videoConsumableContent", offline, videoQuery.data, contentId],
    async () => {
      syncManager.requestSync({ syncType: "user-initiated" })
      // Return offline data if a) we don't have data from the server or b)
      // we're actually offline. In the case of "a" we want don't want to make
      // the user wait for a slow network connection if they have the content
      // downloaded. In the case of "b" we want to ignore cached data from the
      // server as that'll include URLs that won't resolve offline.
      if (!videoQuery.data || offline) {
        const item = await DownloadItem.fromContentId(contentId)
        const consumableContent = await item?.getOfflineConsumableContent()
        return consumableContent?.type === "video"
          ? consumableContent
          : undefined
      }

      return getConsumableContentFromVideoResponse(videoQuery.data)
    },
  )

  const consumableContent = consumableContentQuery.data
  const content = consumableContent?.content
  const videoSeries =
    content?.details.videoSeries.length === 1
      ? content.details.videoSeries[0]
      : undefined
  const isLocked = content?.details.videoMedia?.status === "notAvailable"
  const showAddToLibraryButton = canAddToLibrary({
    manifest: useAppManifest(),
    consumableContent,
  })
  const showDownloadButton =
    consumableContent && canDownloadConsumableContent(consumableContent)

  // Fetch data for the next video
  const videoSeriesQuery = useContent(videoSeries?.id, "videoSeries")
  const allEpisodes = videoSeriesQuery.data?.details.seasons.flatMap(
    (season) => season.episodes,
  )
  const currentEpisodeIndex = allEpisodes?.findIndex(
    (episode) => episode.id === content?.id,
  )
  const nextEpisodeId =
    currentEpisodeIndex !== undefined && currentEpisodeIndex > -1
      ? allEpisodes?.[currentEpisodeIndex + 1]?.id
      : undefined
  const nextVideoQuery = useContent(nextEpisodeId, "video")

  const heading = (
    <Heading level={1} textStyle="headingLarge" color="primary">
      {content?.title || ""}
    </Heading>
  )

  const playButtonStyle = useMemo(
    () =>
      createStyle({
        flex: tokens.videoModule.fullWidthSignUpButton ? 1 : undefined,
      }),
    [tokens],
  )

  const actionButtons = content?.details.videoMedia ? (
    <Row gap="none">
      {content.availability.status === "notAvailable" ? (
        <LockedContentButton
          id={kebabCase(
            `video-locked-content-${`${contentId}-${content?.title || "unknown-title"}`}`,
          )}
          openCheckoutPage={openCheckoutPage}
          availability={content.availability}
          action="watch"
          style={playButtonStyle}
          containerStyle={playButtonStyle}
          externalPurchaseUrl={content.externalPurchaseUrl}
        />
      ) : tokens.appHeader.mode === "desktop" &&
        content.details.videoSeries[0] &&
        (videoSeriesQuery.isLoading || nextVideoQuery.isLoading) ? null : (
        <>
          <ListenIconButton
            id={`video-listen-${`${contentId}-${content?.title || "unknown-title"}`}`}
            consumableContent={consumableContent}
          />
          {showDownloadButton ? (
            <DownloadButton consumableContent={consumableContent} />
          ) : null}
          {showAddToLibraryButton ? (
            <AddToLibraryButton consumableContent={consumableContent} />
          ) : null}
          {nextVideoQuery.data ? (
            <StyledAppLink
              id={kebabCase(
                `next-video-${contentId}-${content?.title || "unknown-title"}`,
              )}
              aria-label={t("Next")}
              to={{
                type: "content",
                contentId: nextVideoQuery.data.id,
                contentType: "video",
              }}
              params={{ autoplay: "1" }}
            >
              <Icon source={icons.skipForward} color="secondary" />
            </StyledAppLink>
          ) : null}
        </>
      )}
    </Row>
  ) : null

  useAsyncViewProps(
    useAsyncViewPropsForQueries(
      offline ? consumableContentQuery : [videoQuery, consumableContentQuery],
    ),
  )

  const publisher = joinContributorNames(content?.contributors, "publisher")
  const shouldShowPublishedDate = Boolean(
    content?.publishedAt && content.dateDisplayStrategy !== "hidden",
  )

  return (
    <AsyncViewOfflineProvider
      offlineStateDisabled={
        // Disable the offline screen if we might have downloaded content to
        // show
        (videoQueryEnabled && videoQuery.isLoading) ||
        consumableContentQuery.isLoading ||
        consumableContent !== undefined
      }
      state="success"
    >
      <AsyncView>
        <VideoPlayerForConsumableContent
          consumableContent={consumableContent}
          nextVideo={nextVideoQuery.data}
          paddingTop={paddingTop}
        />
        <CenteredContent
          paddingTop="large"
          maxWidth={maxWidth || tokens.videoModule.maxWidth}
        >
          {actionButtons && tokens.videoModule.actionButtonsInHeading ? (
            <Row alignItems="flex-start" justifyContent="space-between">
              {heading}
              <Spacer size="medium" horizontal />
              {actionButtons}
            </Row>
          ) : (
            heading
          )}
          {videoSeries ? (
            <>
              <Spacer size="small" />
              <AppLink
                id={`videoseries-${videoSeries.id}-${kebabCase(videoSeries.title)}`}
                to={{
                  type: "content",
                  contentId: videoSeries.id,
                  contentType: "videoSeries",
                }}
                aria-label={videoSeries.title}
              >
                <Row>
                  <Text textStyle="body" numberOfLines={1}>
                    {videoSeries.title}
                  </Text>
                  <Spacer size="tiny" horizontal />
                  <Icon source={icons.chevronRight} size="xsmall" />
                </Row>
              </AppLink>
            </>
          ) : null}
          <Row paddingTop="small">
            {shouldShowPublishedDate && content?.publishedAt ? (
              <MetadataText>
                {formatDate(new Date(content.publishedAt), {
                  strategy: content.dateDisplayStrategy || "fullDate",
                  i18n,
                })}
              </MetadataText>
            ) : null}
            <ProgressForConsumableContent
              consumableContent={consumableContent}
              includeDurationLabel
              includeFinishedBadge
              childrenBefore={
                shouldShowPublishedDate ? <MetadataSpacer /> : null
              }
            />
          </Row>
          {publisher ? <MetadataText>{publisher}</MetadataText> : null}
          {actionButtons && !tokens.videoModule.actionButtonsInHeading ? (
            <ActionButtonContainer paddingTop={isLocked ? "medium" : "xsmall"}>
              {actionButtons}
            </ActionButtonContainer>
          ) : null}
          {content?.description ? (
            <Box
              paddingTop={
                actionButtons &&
                !tokens.videoModule.actionButtonsInHeading &&
                !isLocked
                  ? "xsmall"
                  : "large"
              }
            >
              <TextToggle
                textStyle="body"
                maxHeight={presentation === "solo" ? 260 : 160}
                maxAllowedHeadingLevel={2}
              >
                {content.description}
              </TextToggle>
            </Box>
          ) : null}
        </CenteredContent>
      </AsyncView>
    </AsyncViewOfflineProvider>
  )
}
